// Bootstrap 5 removed the jQuery plugin interface ($(el).modal(), .tooltip(), …).
// This bridge re-exposes those jQuery methods on top of the BS5 vanilla API so the
// existing call sites keep working without rewriting 28 Stimulus controllers at once.
//
// Supported forms (matching the BS4 jQuery API used across the app):
//   $(el).modal()                 -> getOrCreateInstance(el)
//   $(el).modal({backdrop:'static'}) -> getOrCreateInstance(el, options)
//   $(el).modal('show'|'hide'|'toggle'|'dispose'|'handleUpdate')
//
// Events keep their BS5 names (shown.bs.modal, hidden.bs.modal, …) and still
// bubble as DOM events, so jQuery `.on('hidden.bs.modal', …)` continues to work.
//
// This is a deliberate compatibility layer (DDT-3772). Controllers can be ported
// to the native `bootstrap.*` API incrementally; remove a bridge entry once its
// component has no remaining jQuery callers.
import $ from 'jquery';
import { Modal, Tooltip, Popover, Tab, Collapse, Toast, Dropdown, Alert } from 'bootstrap';

function bridge(name, Ctor, showOnInit) {
  $.fn[name] = function bridged(config, ...args) {
    return this.each(function each() {
      if (typeof config === 'string') {
        // Method call: $(el).modal('show'). Don't auto-create on dispose/hide of a
        // never-initialised element.
        const existing = Ctor.getInstance(this);
        const inst = existing || (config === 'dispose' ? null : Ctor.getOrCreateInstance(this));
        if (inst && typeof inst[config] === 'function') inst[config](...args);
      } else {
        // Init call: $(el).modal({...}) or $(el).modal(). In BS4 this also SHOWED
        // the modal (unless {show:false}); BS5 separates init from show, so for
        // `showOnInit` components we call .show() to match the old behaviour.
        const opts = config || {};
        const inst = Ctor.getOrCreateInstance(this, opts);
        if (showOnInit && opts.show !== false && typeof inst.show === 'function') inst.show();
      }
    });
  };
}

// showOnInit=true only for modal: BS4 `$(el).modal(opts)` opened it. Tooltips/
// popovers/dropdowns init silently and show on interaction.
bridge('modal', Modal, true);
bridge('tooltip', Tooltip);
bridge('popover', Popover);
bridge('tab', Tab);
bridge('collapse', Collapse);
bridge('toast', Toast);
bridge('dropdown', Dropdown);
bridge('alert', Alert);

// BS5's declarative data-API (the click handlers for data-bs-toggle/data-bs-dismiss)
// registers itself at module scope when `bootstrap` is imported, and IS present in
// this build — so re-implementing it here made every declarative trigger fire twice.
// Dropdown.toggle() has no transition guard, so the second toggle closed the menu
// again and dropdowns never opened. Native handles the declarative side; the $.fn
// bridges above remain for the app's imperative jQuery call sites.

// The two listeners below must run before Bootstrap's own click handlers. Bootstrap
// registers its data-API handlers in the CAPTURE phase on `document`, when `bootstrap`
// is imported, so a capture listener added to `document` here runs after them. Capture
// listeners on `window` run first.

// Safety net for the declarative modal toggle. Before opening a modal, BS5 closes any
// `.modal.show` already on the page with `Modal.getInstance(el).hide()` — and crashes
// when that element has no instance, so NO modal can open. A static `class="modal fade
// show"` in a template did exactly that on every page (#onlineNowModal, prod markup that
// BS4's jQuery plugin never tripped on). Give such elements an instance first; hide()
// on a modal that isn't open is a no-op.
window.addEventListener('click', (event) => {
  if (!event.target.closest || !event.target.closest('[data-bs-toggle="modal"]')) return;
  document.querySelectorAll('.modal.show').forEach((el) => Modal.getOrCreateInstance(el));
}, true);

// Summernote 0.8 builds its toolbar dropdowns (the paragraph/alignment menu) with the
// Bootstrap 4 attribute `data-toggle="dropdown"`, which Bootstrap 5 ignores, so the
// menus never opened. Give the toggle the Bootstrap 5 attribute before Bootstrap's own
// handler sees the click; from then on it is a normal dropdown, closed by an outside
// click or Escape like any other.
window.addEventListener('click', (event) => {
  const toggle = event.target.closest && event.target.closest('.note-editor [data-toggle="dropdown"]');
  if (toggle && !toggle.hasAttribute('data-bs-toggle')) toggle.setAttribute('data-bs-toggle', 'dropdown');
}, true);
